/**
 * Sparkline minimalista en SVG puro — sin dependencias externas.
 *
 * Diseñado para vivir dentro de las cards del dashboard:
 * altura ~28px, ancho fluido (`width="100%"`), línea + área degradada.
 *
 * Si todos los valores son iguales (o hay <2 puntos) renderiza una línea
 * horizontal a media altura para no romper el layout.
 */
interface SparklineProps {
  values: number[];
  /** color CSS de la línea (también se usa para el degradado del área) */
  color?: string;
  /** alto en px (default 28) */
  height?: number;
  /** Etiqueta accesible — se anuncia a lectores de pantalla */
  ariaLabel?: string;
  className?: string;
}

export function Sparkline({
  values,
  color = 'currentColor',
  height = 28,
  ariaLabel,
  className,
}: SparklineProps) {
  // Sanitizar entrada: si no hay datos suficientes, mostrar línea plana
  const data = values.length >= 2 ? values : values.length === 1 ? [values[0], values[0]] : [0, 0];

  const width = 100; // viewBox normalizado, el SVG escala via width="100%"
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1; // evita /0 cuando todos los valores son iguales

  // Mapear valores → puntos SVG
  // x: equiespaciado entre [0, width]
  // y: invertir (SVG crece hacia abajo) → max=arriba, min=abajo
  // pad vertical 2px para que no toque los bordes
  const padY = 2;
  const usableH = height - padY * 2;
  const points = data.map((v, i) => {
    const x = (i / (data.length - 1)) * width;
    const norm = (v - min) / range; // 0..1
    const y = padY + (1 - norm) * usableH;
    return [x, y] as const;
  });

  const linePath = points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(2)},${y.toFixed(2)}`).join(' ');
  const areaPath = `${linePath} L${width},${height} L0,${height} Z`;

  // ID único para el gradient (evita colisión si hay varios sparklines)
  const gradId = `spark-grad-${Math.random().toString(36).slice(2, 9)}`;

  return (
    <svg
      role="img"
      aria-label={ariaLabel}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      className={className}
      style={{ width: '100%', height, color, display: 'block' }}
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="currentColor" stopOpacity="0.35" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill={`url(#${gradId})`} />
      <path d={linePath}  fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      {/* punto final destacado */}
      {points.length > 0 && (
        <circle
          cx={points[points.length - 1][0]}
          cy={points[points.length - 1][1]}
          r="1.8"
          fill="currentColor"
        />
      )}
    </svg>
  );
}
